import { useClientTranslation } from '@/lib/core';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import HTMLParser from 'html-react-parser';
import { TitleRteItemElementContentModel } from '@/services/platform/api/model';

export interface ProfileContactUsAssistanceProps {
  subTitle?: string;
  // Concierge
  conciergeSectionTitle?: string;
  conciergeDescription?: string;
  conciergeButtonLabel?: string;
  onContactConcierge?: () => void;
  // Call us
  callUsSectionTitle?: string;
  callUsSchedule?: string;
  callUsButtonLabel?: string;
  callbackButtonLabel?: string;
  onCallUs?: () => void;
  onRequestCallback?: () => void;
  // Email us
  emailUsSectionTitle?: string;
  emailUsDescription?: string;
  emailUsButtonLabel?: string;
  onSendEmail?: () => void;
  // FAQ
  faqSectionTitle?: string;
  faqDescription?: string;
  faqList?: TitleRteItemElementContentModel[];
}

export function ProfileContactUsAssistance({
  subTitle,
  conciergeSectionTitle,
  conciergeDescription,
  conciergeButtonLabel,
  onContactConcierge,
  callUsSectionTitle,
  callUsSchedule,
  callUsButtonLabel,
  callbackButtonLabel,
  onCallUs,
  onRequestCallback,
  emailUsSectionTitle,
  emailUsDescription,
  emailUsButtonLabel,
  onSendEmail,
  faqSectionTitle,
  faqDescription,
  faqList = [],
}: ProfileContactUsAssistanceProps) {
  const t = useClientTranslation();

  return (
    <div className="flex flex-col gap-6 overflow-y-auto px-4 pt-4 pb-8">
      <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
        {subTitle ?? t('App.Labels.AssistanceSubTitle')}
      </p>

      {/* Concierge */}
      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-semibold tracking-[0.5px]">
            {conciergeSectionTitle ?? t('App.Labels.AssistanceConciergeTitle')}
          </p>
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px]">
            {conciergeDescription ?? t('App.Labels.AssistanceConciergeDescription')}
          </p>
        </div>
        <Button
          variant="primary-solid"
          palette="dark"
          size="md"
          className="w-full"
          onClick={onContactConcierge}
        >
          {conciergeButtonLabel ?? t('App.Labels.AssistanceConciergeButton')}
        </Button>
      </section>

      {/* Call us */}
      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-semibold tracking-[0.5px]">
            {callUsSectionTitle ?? t('App.Labels.AssistanceCallUsTitle')}
          </p>
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] whitespace-pre-line">
            {callUsSchedule ?? t('App.Labels.AssistanceCallUsSchedule')}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <Button variant="secondary-outline" palette="dark" size="md" className="w-full" onClick={onCallUs}>
            {callUsButtonLabel ?? t('App.Labels.AssistanceCallUsButton')}
          </Button>
          <Button
            variant="secondary-outline"
            palette="dark"
            size="md"
            className="w-full"
            onClick={onRequestCallback}
          >
            {callbackButtonLabel ?? t('App.Labels.AssistanceCallbackButton')}
          </Button>
        </div>
      </section>

      {/* Email us */}
      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-semibold tracking-[0.5px]">
            {emailUsSectionTitle ?? t('App.Labels.AssistanceEmailUsTitle')}
          </p>
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px]">
            {emailUsDescription ?? t('App.Labels.AssistanceEmailUsDescription')}
          </p>
        </div>
        <Button variant="secondary-outline" palette="dark" size="md" className="w-full" onClick={onSendEmail}>
          {emailUsButtonLabel ?? t('App.Labels.AssistanceEmailUsButton')}
        </Button>
      </section>

      {/* FAQ */}
      {(faqList.length > 0 || faqSectionTitle || faqDescription) && (
        <section className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-semibold tracking-[0.5px]">
              {faqSectionTitle ?? t('App.Labels.AssistanceFaqTitle')}
            </p>
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px]">
              {faqDescription ?? t('App.Labels.AssistanceFaqDescription')}
            </p>
          </div>
          {faqList.length > 0 && (
            <div className="border-b-[0.5px] border-[#4e4e4e]">
              <Accordion type="single" collapsible>
                {faqList.map((item, index) => (
                  <AccordionItem
                    key={`${index}-${item.properties?.title}`}
                    value={item.properties?.title ?? ''}
                  >
                    <AccordionTrigger>{item.properties?.title}</AccordionTrigger>
                    {item.properties?.text?.markup && (
                      <AccordionContent>
                        <div className="font-pp-neue-montreal text-jncquoi-neutral-1000 pb-3 text-[12px] tracking-[0.5px]">
                          {HTMLParser(item.properties.text.markup)}
                        </div>
                      </AccordionContent>
                    )}
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
